/**
 * Shared helpers + UI primitives + constants for MyProfile.
 */
import React from 'react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Clock, Loader2, CheckCircle2, XCircle, Edit3, AlertTriangle,
} from 'lucide-react';

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
  } catch {
    return dateStr;
  }
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch {
    return dateStr;
  }
};

export const EDITABLE_FIELDS = {
  contact: {
    label: 'Kontaktdaten',
    fields: {
      private_email: { label: 'E-Mail (privat)', type: 'email' },
      work_email: { label: 'E-Mail (geschäftlich)', type: 'email' },
      private_phone: { label: 'Telefon (privat)', type: 'tel' },
      mobile_phone: { label: 'Telefon (mobil)', type: 'tel' },
      work_phone: { label: 'Telefon (geschäftlich)', type: 'tel' },
      street: { label: 'Straße & Hausnummer', type: 'text' },
      address_addition: { label: 'Adresszusatz', type: 'text' },
      postal_code: { label: 'PLZ', type: 'text' },
      city: { label: 'Stadt', type: 'text' },
      country: { label: 'Land', type: 'text' },
    },
  },
  bank: {
    label: 'Bankverbindung',
    fields: {
      bank_name: { label: 'Bank', type: 'text' },
      iban: { label: 'IBAN', type: 'text' },
      bic: { label: 'BIC', type: 'text' },
      account_holder: { label: 'Kontoinhaber', type: 'text' },
    },
  },
  emergency: {
    label: 'Notfallkontakt',
    fields: {
      emergency_contact_name: { label: 'Name', type: 'text' },
      emergency_contact_phone: { label: 'Telefon', type: 'tel' },
      emergency_contact_relationship: { label: 'Beziehung', type: 'text' },
    },
  },
};

export const STATUS_CONFIG = {
  pending: { label: 'Eingereicht', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'In Bearbeitung', color: 'bg-blue-500', icon: Loader2 },
  approved: { label: 'Genehmigt', color: 'bg-green-500', icon: CheckCircle2 },
  rejected: { label: 'Abgelehnt', color: 'bg-red-500', icon: XCircle },
};

export const InfoRow = ({ icon: Icon, label, value, sensitive = false, editable = false, onEdit }) => {
  const isJsx = value !== null && typeof value === 'object';
  return (
    <div className="flex items-start gap-3 py-2 group">
      <Icon className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {isJsx ? (
          <div className={`font-medium ${sensitive ? 'blur-sm hover:blur-none transition-all cursor-pointer' : ''}`}>
            {value}
          </div>
        ) : (
          <p className={`font-medium ${sensitive ? 'blur-sm hover:blur-none transition-all cursor-pointer' : ''}`}>
            {value || '-'}
          </p>
        )}
      </div>
      {editable && (
        <Button
          variant="ghost"
          size="sm"
          className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
          onClick={onEdit}
          data-testid={`edit-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Edit3 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} text-white`}>
      <Icon className={`h-3 w-3 mr-1 ${status === 'in_progress' ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

/**
 * ExpiryBadge — zeigt einen Status für `expiry_date`:
 *   - rot (destructive): bereits abgelaufen
 *   - amber (warning): laeuft in <= 30 Tagen ab
 *   - neutral: laeuft in > 30 Tagen ab (Default-Outline-Badge mit „Gueltig bis")
 * Akzeptiert `expiry` als ISO-String (YYYY-MM-DD oder volles ISO).
 */
export const ExpiryBadge = ({ expiry, prefix = 'Gültig bis' }) => {
  if (!expiry) return null;
  let target;
  try {
    target = parseISO(typeof expiry === 'string' ? expiry.slice(0, 10) : expiry);
  } catch {
    return null;
  }
  const days = differenceInCalendarDays(target, new Date());
  const formatted = format(target, 'dd.MM.yyyy', { locale: de });

  if (days < 0) {
    return (
      <Badge variant="destructive" className="text-xs gap-1" data-testid="expiry-badge-expired">
        <AlertTriangle className="h-3 w-3" />
        Abgelaufen: {formatted}
      </Badge>
    );
  }
  if (days <= 30) {
    const label = days === 0
      ? 'Läuft heute ab'
      : `Läuft in ${days} Tag${days === 1 ? '' : 'en'} ab`;
    return (
      <Badge
        className="text-xs gap-1 bg-amber-500 hover:bg-amber-500/90 text-white border-amber-600"
        data-testid="expiry-badge-warning"
      >
        <AlertTriangle className="h-3 w-3" />
        {label} ({formatted})
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs" data-testid="expiry-badge-ok">
      {prefix}: {formatted}
    </Badge>
  );
};
